import { useCallback, useEffect } from 'react';
import { fitCamera, CARD_H, CARD_W } from '../geometry';
import { afterNextPaint, BOARD_FRAME, BOARD_GO_TO_START, BOARD_PAN_TO } from '../events';
import { sizeOf } from '../utils/nodeSize';
import { markBoardPictureCold, paintDots, place, placeWires, settleBoardWires, warmBoardPicture } from '../utils/placeCamera';

/** Glide the camera. The clock starts on the first frame so a blocked render cannot skip the pan. */
export function useBoardCamera({
  tasks,
  wrapRef,
  worldRef,
  cameraRef,
  flyFrameRef,
  tasksRef,
  positionsRef,
  boxesRef,
  onSelectionChangeRef,
  gestureRef,
}) {
  const flyCamera = useCallback((target) => {
    if (flyFrameRef.current) cancelAnimationFrame(flyFrameRef.current);
    const start = { ...cameraRef.current };
    let startTime = 0;
    const ease = (t) => 1 - (1 - t) ** 3;
    const step = (now) => {
      // The new card's render can block the first frame. Start the clock then,
      // or that delay spends the whole pan and the camera arrives in one jump.
      if (!startTime) startTime = now;
      const progress = Math.min((now - startTime) / 300, 1);
      const eased = ease(progress);
      cameraRef.current = {
        x: start.x + (target.x - start.x) * eased,
        y: start.y + (target.y - start.y) * eased,
        zoom: start.zoom + (target.zoom - start.zoom) * eased,
      };
      place(worldRef.current, cameraRef.current);
      if (progress < 1) {
        flyFrameRef.current = requestAnimationFrame(step);
      } else {
        flyFrameRef.current = 0;
        settleBoardWires(worldRef.current, cameraRef.current);
      }
    };
    flyFrameRef.current = requestAnimationFrame(step);
  }, [cameraRef, flyFrameRef, worldRef]);

  useEffect(() => {
    const world = worldRef.current;
    if (!world) return undefined;
    const parent = world.parentElement;
    const wires = parent?.querySelector('.board-view__wires') || null;
    const connection = parent?.querySelector('.board-view__connection') || null;
    const dots = parent?.querySelector('.board-view__dots') || null;
    const onPan = (event) => {
      const detail = event.detail || {};
      if (!Number.isFinite(detail.x) || !Number.isFinite(detail.y)) return;
      cameraRef.current = {
        x: detail.x,
        y: detail.y,
        zoom: detail.zoom || cameraRef.current.zoom,
      };
      // The navigator writes the card layer itself. Dots and wires sit beside it,
      // so they have to follow the same numbers.
      if (wires) placeWires(wires, cameraRef.current);
      const liveConnection = connection || parent?.querySelector('.board-view__connection');
      if (liveConnection) liveConnection.style.transform = world.style.transform;
      paintDots(dots, cameraRef.current);
    };
    world.addEventListener('panupdate', onPan);
    if (!flyFrameRef.current) place(world, cameraRef.current);
    return () => world.removeEventListener('panupdate', onPan);
  }, [tasks, worldRef, cameraRef, flyFrameRef]);

  // The graphics card drops the board picture while the window is minimized or
  // in the background. Rebuild it the moment the window is back.
  useEffect(() => {
    const onCold = () => markBoardPictureCold();
    const onWarm = () => warmBoardPicture(worldRef.current);
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') onCold();
      else onWarm();
    };
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('freeze', onCold);
    document.addEventListener('resume', onWarm);
    window.addEventListener('blur', onCold);
    window.addEventListener('focus', onWarm);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('freeze', onCold);
      document.removeEventListener('resume', onWarm);
      window.removeEventListener('blur', onCold);
      window.removeEventListener('focus', onWarm);
    };
  }, [worldRef]);

  // Navigator drags are not the board's own pointer handler. Hold layout off
  // for the whole gesture so a measurement cannot land on the first move.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || !gestureRef) return undefined;
    let fromNavigator = false;
    const onDown = (event) => {
      if (event.button !== 0) return;
      const target = event.target;
      if (!(target instanceof Element) || !target.closest('.flowchart-minimap')) return;
      fromNavigator = true;
      gestureRef.current = true;
    };
    const onUp = () => {
      if (!fromNavigator) return;
      fromNavigator = false;
      gestureRef.current = false;
    };
    wrap.addEventListener('mousedown', onDown);
    window.addEventListener('mouseup', onUp);
    return () => {
      wrap.removeEventListener('mousedown', onDown);
      window.removeEventListener('mouseup', onUp);
    };
  }, [wrapRef, gestureRef]);

  const goToTask = useCallback((taskId) => {
    const pos = positionsRef.current?.get(taskId);
    const wrap = wrapRef.current;
    if (pos && wrap) {
      const task = tasksRef.current.find((item) => item.id === taskId);
      const size = sizeOf(task, boxesRef.current);
      const currentZoom = cameraRef.current.zoom || 1;
      const zoom = currentZoom < 0.7 || currentZoom > 1.3 ? 1 : currentZoom;
      flyCamera({
        zoom,
        x: wrap.clientWidth / 2 - (pos.x + size.width / 2) * zoom,
        y: wrap.clientHeight / 2 - (pos.y + size.height / 2) * zoom,
      });
    }
    afterNextPaint(() => onSelectionChangeRef.current?.(new Set([taskId])));
  }, [flyCamera, wrapRef, positionsRef, tasksRef, boxesRef, cameraRef, onSelectionChangeRef]);

  useEffect(() => {
    const onFocus = (event) => {
      const { missionId, view } = event.detail || {};
      if (view !== 'board' || !missionId) return;
      goToTask(missionId);
    };
    window.addEventListener('focusMission', onFocus);
    return () => window.removeEventListener('focusMission', onFocus);
  }, [goToTask]);

  useEffect(() => {
    const onGoToStart = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      flyCamera(fitCamera(tasksRef.current, positionsRef.current, wrap.clientWidth, wrap.clientHeight));
    };
    const onPanTo = (event) => {
      const wrap = wrapRef.current;
      const detail = event.detail || {};
      if (!wrap || !Number.isFinite(detail.x) || !Number.isFinite(detail.y)) return;
      const width = detail.width || CARD_W;
      const height = detail.height || CARD_H;
      const currentZoom = cameraRef.current.zoom || 1;
      const zoom = detail.adjustZoom && (currentZoom < 0.7 || currentZoom > 1.3) ? 1 : currentZoom;
      flyCamera({
        zoom,
        x: wrap.clientWidth / 2 - (detail.x + width / 2) * zoom,
        y: wrap.clientHeight / 2 - (detail.y + height / 2) * zoom,
      });
      if (detail.taskId) afterNextPaint(() => onSelectionChangeRef.current?.(new Set([detail.taskId])));
    };
    const onFrame = (event) => {
      const wrap = wrapRef.current;
      const detail = event.detail || {};
      if (!wrap || !detail.tasks?.length || !detail.positions) return;
      const positions = new Map();
      Object.entries(detail.positions).forEach(([id, pos]) => {
        if (pos) positions.set(id, pos);
      });
      flyCamera(fitCamera(detail.tasks, positions, wrap.clientWidth, wrap.clientHeight, boxesRef.current));
    };
    window.addEventListener(BOARD_GO_TO_START, onGoToStart);
    window.addEventListener(BOARD_PAN_TO, onPanTo);
    window.addEventListener(BOARD_FRAME, onFrame);
    return () => {
      window.removeEventListener(BOARD_GO_TO_START, onGoToStart);
      window.removeEventListener(BOARD_PAN_TO, onPanTo);
      window.removeEventListener(BOARD_FRAME, onFrame);
      if (flyFrameRef.current) cancelAnimationFrame(flyFrameRef.current);
    };
  }, [flyCamera, wrapRef, tasksRef, positionsRef, cameraRef, flyFrameRef, onSelectionChangeRef]);

  return { goToTask };
}
